import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { findService, services } from "@/data/services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = findService(slug);

  if (!service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#71717a] mb-3">
          404
        </div>
        <h1 className="font-heading text-3xl text-white">
          Service not found
        </h1>
        <Link to="/services" className="btn-outline mt-8">
          Back to services
        </Link>
      </div>
    );
  }

  const Icon = service.icon;
  const related = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div>
      <section
        data-testid={`service-hero-${service.slug}`}
        className="pt-16 md:pt-24 pb-20 md:pb-28 border-b border-[#27272a] relative overflow-hidden"
      >
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-start">
          <div>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#71717a] hover:text-[#C8A24B] transition-colors mb-6"
            >
              ← Services
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 border border-[#27272a] flex items-center justify-center">
                <Icon size={22} className="text-[#C8A24B]" strokeWidth={1.4} />
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B]">
                Service
              </div>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05]">
              {service.title}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#C8A24B] font-medium">
              {service.tagline}
            </p>
            <div className="mt-8 space-y-4">
              {service.body.map((p, i) => (
                <p key={i} className="text-[#d4d4d8] leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-gold">
                Talk about this service <ArrowRight size={16} />
              </Link>
              <Link to="/packages" className="btn-outline">
                See packages
              </Link>
            </div>
          </div>
          <div className="border border-[#27272a] bg-[#0e0e0e] p-8 rounded-sm relative">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B]" />
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-4">
              What's Included
            </div>
            <ul className="space-y-3">
              {service.includes.map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <Check size={14} className="text-[#C8A24B] flex-shrink-0 mt-1" />
                  <span className="text-[#d4d4d8]">{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 md:py-28 border-b border-[#27272a] bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-4">
            Outcomes
          </div>
          <h2 className="font-heading text-3xl md:text-4xl text-white max-w-3xl leading-tight">
            What clients gain from this service.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {service.outcomes.map((o, i) => (
              <div
                key={i}
                className="border border-[#27272a] bg-[#0a0a0a] p-6 rounded-sm"
              >
                <div className="font-heading text-[#C8A24B] text-xs tracking-widest mb-3">
                  0{i + 1}
                </div>
                <p className="text-[#d4d4d8] leading-relaxed">{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-3">
                Related
              </div>
              <h2 className="font-heading text-3xl text-white">
                Other services
              </h2>
            </div>
            <Link
              to="/services"
              className="hidden md:inline-flex text-[#C8A24B] text-sm items-center gap-2 hover:gap-3 transition-all"
            >
              All services <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((s) => {
              const RIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group border border-[#27272a] hover:border-[#C8A24B]/50 bg-[#0e0e0e] p-8 rounded-sm transition-all"
                >
                  <div className="w-11 h-11 border border-[#27272a] flex items-center justify-center mb-5 group-hover:border-[#C8A24B]">
                    <RIcon size={18} className="text-[#C8A24B]" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-heading text-lg text-white mb-2">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[#a1a1aa] leading-relaxed">{s.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
